import React, { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { KeyRound, Copy, Check } from "lucide-react";
import DeleteConfirm from "@/components/DeleteConfirm";
import { useAuth } from "@/context/AuthContext";
const ADMIN_USER_ID = "9999392934";

const MODULES = [
  ["dispatch_register", "Dispatch Register"],
  ["reports", "Reports"],
  ["customers", "Customer Master"],
  ["transporters", "Transporter Master"],
  ["vehicles", "Vehicle Master"],
  ["products", "Product Master"],
  ["pod", "POD"],
  ["returns", "Returns"],
  ["users", "User Management"],
];

const DEFAULT_PERMS = Object.fromEntries(MODULES.map(([k]) => [k, { view: true, edit: false }]));

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ user_id: "", email: "", password: "", name: "", role: "user", permissions: DEFAULT_PERMS });

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setF({ user_id: "", email: "", password: "", name: "", role: "user", permissions: DEFAULT_PERMS }); setCreating(true); };
  const openEdit = (u) => { setF({ ...u, password: "", permissions: u.permissions || DEFAULT_PERMS }); setEditing(u.id); };

  const submit = async () => {
    try {
      if (editing) {
        const body = { name: f.name, email: f.email || null, role: f.role, permissions: f.permissions };
        if (f.password) body.password = f.password;
        await api.patch(`/users/${editing}`, body);
      } else {
        if (!f.name.trim()) return toast.error("Name is required");
        if (!/^\d{10}$/.test(f.user_id.trim())) return toast.error("User ID must be a 10-digit phone number");
        if (!f.password) return toast.error("Password is required");
        if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return toast.error("Invalid email format");
        await api.post("/users", { ...f, user_id: f.user_id.trim(), email: f.email.trim() || null });
      }
      toast.success("Saved"); setCreating(false); setEditing(null); load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  const togglePerm = (mod, action) => setF((s) => ({
    ...s, permissions: { ...s.permissions, [mod]: { ...s.permissions[mod], [action]: !s.permissions[mod]?.[action] } }
  }));

  const toggleActive = async (u, active) => { await api.patch(`/users/${u.id}`, { active }); load(); };
  const togglePodOptout = async (u, receive) => {
    await api.patch(`/users/${u.id}`, { pod_email_optout: !receive });
    load(); loadPreview();
  };

  const [preview, setPreview] = useState(null);
  const loadPreview = async () => {
    try { const r = await api.post("/notifications/overdue-pod-preview"); setPreview(r.data); } catch { /* not admin */ }
  };
  useEffect(() => { loadPreview(); }, []);

  const [resetInfo, setResetInfo] = useState(null); // { link, expires_at, email_sent, user }
  const [copied, setCopied] = useState(false);
  const sendReset = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/reset-token`);
      setResetInfo(r.data);
      setCopied(false);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(resetInfo.link); setCopied(true); toast.success("Link copied"); }
    catch { toast.error("Copy failed – select and copy manually"); }
  };

  const open = creating || !!editing;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Users & Permissions</h1>
          <p className="text-slate-500 text-sm mt-1">Create users and grant granular View/Edit access per module.</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="add-user-btn">+ New User</Button>
      </div>

      {preview && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="pod-email-recipients-summary">
          <div className="font-medium text-blue-900">Daily Pending POD email</div>
          <div className="text-slate-700"><b>{preview.recipients.length}</b> recipient{preview.recipients.length === 1 ? "" : "s"}</div>
          <div className="text-slate-600">Next payload: <b>{preview.count}</b> row{preview.count === 1 ? "" : "s"} · {preview.total_weight} Kg · max {preview.max_age}d</div>
          {preview.excluded?.length > 0 && (
            <div className="text-xs text-slate-500">
              Excluded: {preview.excluded.slice(0, 4).map((e) => `${e.name} (${e.reason})`).join(", ")}{preview.excluded.length > 4 ? " …" : ""}
            </div>
          )}
        </div>
      )}

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
            <tr>{["Name", "User ID", "Email", "Role", "Status", "POD Email", "Actions"].map((h) => <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${i}`}>
                <td className="px-3 py-2 font-medium">{u.name}</td>
                <td className="px-3 py-2 font-mono-num">{u.user_id || "—"}</td>
                <td className="px-3 py-2">{u.email || "—"}</td>
                <td className="px-3 py-2"><Badge className={u.role === "admin" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}>{u.role}</Badge></td>
                <td className="px-3 py-2"><Badge className={u.active ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}>{u.active ? "Active" : "Inactive"}</Badge></td>
                <td className="px-3 py-2">
                  {u.email ? (
                    <div className="flex items-center gap-2">
                      <Switch checked={!u.pod_email_optout} onCheckedChange={(v) => togglePodOptout(u, v)} data-testid={`pod-email-toggle-${i}`} />
                      <span className={`text-xs ${u.pod_email_optout ? "text-slate-500" : "text-emerald-700 font-medium"}`}>{u.pod_email_optout ? "Excluded" : "Included"}</span>
                    </div>
                  ) : <span className="text-xs text-slate-400 italic">No email</span>}
                </td>
                <td className="px-3 py-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${i}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => sendReset(u)} data-testid={`reset-user-${i}`} title="Generate one-time password reset link">
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset
                  </Button>
                  <Switch checked={u.active} onCheckedChange={(v) => toggleActive(u, v)} data-testid={`toggle-user-${i}`} />
                  {u.user_id !== ADMIN_USER_ID && u.id !== me?.id && (
                    <DeleteConfirm url={`/users/${u.id}`}
                      title="Delete User"
                      description={<div>Delete user <b>{u.name}</b> ({u.user_id})? Their pending password reset links will also be revoked.</div>}
                      onDeleted={load}
                      testid={`delete-user-${i}`} iconOnly />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label>User ID (Phone Number) *</Label><Input value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} disabled={!!editing} placeholder="10-digit mobile" inputMode="numeric" data-testid="user-userid-input" /></div>
            <div><Label>Email <span className="text-xs text-slate-400">(optional)</span></Label><Input type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" placeholder="optional" /></div>
            <div><Label>{editing ? "New Password (blank = keep)" : "Password *"}</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /></div>
            <div>
              <Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {f.role === "user" && (
            <div className="mt-4">
              <div className="text-sm font-semibold mb-2">Permissions</div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                    <tr><th className="px-3 py-2 text-left">Module</th><th className="px-3 py-2 text-center">View</th><th className="px-3 py-2 text-center">Edit</th></tr>
                  </thead>
                  <tbody>
                    {MODULES.map(([k, l]) => (
                      <tr key={k} className="border-t">
                        <td className="px-3 py-2">{l}</td>
                        <td className="px-3 py-2 text-center"><Checkbox checked={!!f.permissions[k]?.view} onCheckedChange={() => togglePerm(k, "view")} data-testid={`perm-${k}-view`} /></td>
                        <td className="px-3 py-2 text-center"><Checkbox checked={!!f.permissions[k]?.edit} onCheckedChange={() => togglePerm(k, "edit")} data-testid={`perm-${k}-edit`} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submit} data-testid="save-user-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetInfo} onOpenChange={(o) => { if (!o) setResetInfo(null); }}>
        <DialogContent className="max-w-lg" data-testid="reset-link-dialog">
          <DialogHeader><DialogTitle>One-time reset link</DialogTitle></DialogHeader>
          {resetInfo && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">For</div>
                <div className="font-medium mt-0.5">{resetInfo.user?.name} · <span className="font-mono-num">{resetInfo.user?.user_id}</span></div>
                {resetInfo.user?.email && <div className="text-xs text-slate-500 mt-0.5">{resetInfo.user.email}</div>}
              </div>
              <div>
                <Label>Reset link (expires in 24 hours, single-use)</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={resetInfo.link} className="font-mono text-xs" data-testid="reset-link-value" />
                  <Button type="button" variant="outline" onClick={copyLink} data-testid="reset-link-copy">
                    {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className={`rounded-md px-3 py-2 text-xs border ${resetInfo.email_sent ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                {resetInfo.email_sent
                  ? `Emailed to ${resetInfo.user?.email}. You can also share the link over SMS/WhatsApp.`
                  : resetInfo.user?.email
                    ? "Could not email – share this link via SMS/WhatsApp instead."
                    : "This user has no email on file – share the link via SMS/WhatsApp."}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResetInfo(null)} data-testid="reset-link-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
